import React from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import CommonDialog from "../../../common/CommonDialog";
import { uploadVideoToLibrary } from "../api/uploadVideoToLibrary";

const MAX_BYTES = 200 * 1024 * 1024;
const ACCEPT = "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov";

function formatBytes(n) {
  if (!Number.isFinite(n) || n < 1) return "";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * @param {{ open: boolean; onClose: () => void; lang?: object; hitAxios: Function; onUploaded?: (job: object) => void; notify?: (msg: string, ok?: boolean) => void }} props
 */
export default function VideoLibraryUploadDialog({
  open,
  onClose,
  lang,
  hitAxios,
  onUploaded,
  notify,
}) {
  const theme = useTheme();
  const inputRef = React.useRef(null);
  const [file, setFile] = React.useState(null);
  const [title, setTitle] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState("");

  const reset = React.useCallback(() => {
    setFile(null);
    setTitle("");
    setProgress(0);
    setError("");
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  React.useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const pickFile = (list) => {
    const f = list?.[0];
    if (!f) return;
    setError("");
    if (f.size > MAX_BYTES) {
      setError(lang?.videoLibraryUploadTooLarge || "Video must be 200 MB or smaller.");
      return;
    }
    const okType =
      /^video\/(mp4|webm|quicktime)$/i.test(f.type) ||
      /\.(mp4|webm|mov)$/i.test(f.name || "");
    if (!okType) {
      setError(lang?.videoLibraryUploadType || "Please upload MP4, WebM, or MOV.");
      return;
    }
    setFile(f);
    if (!title.trim()) {
      const base = (f.name || "").replace(/\.[^.]+$/, "");
      if (base) setTitle(base);
    }
  };

  const handleSubmit = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setError("");
    setProgress(0);
    try {
      const job = await uploadVideoToLibrary(hitAxios, file, {
        title: title.trim() || undefined,
        onUploadProgress: (e) => {
          if (e.total) {
            setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
          }
        },
      });
      setProgress(100);
      notify?.(
        lang?.videoLibraryUploadSuccess || "Video added to your library.",
        true,
      );
      onUploaded?.(job);
      onClose();
    } catch (err) {
      const msg =
        err?.message || lang?.videoLibraryUploadFailed || "Could not upload video.";
      setError(msg);
      notify?.(msg, false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <CommonDialog
      open={open}
      onClose={uploading ? undefined : onClose}
      title={lang?.videoLibraryUploadTitle || "Upload video"}
      icon={CloudUploadOutlined}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {lang?.videoLibraryUploadHint ||
            "Add your own MP4, WebM, or MOV (up to 200 MB). It will appear in your library and can be used when stitching clips."}
        </Typography>

        <Box
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (!uploading) pickFile(e.dataTransfer?.files);
          }}
          sx={{
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
            borderRadius: 2,
            py: 4,
            px: 2,
            textAlign: "center",
            cursor: uploading ? "default" : "pointer",
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            opacity: uploading ? 0.7 : 1,
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            hidden
            disabled={uploading}
            onChange={(e) => pickFile(e.target.files)}
          />
          <CloudUploadOutlined sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {file
              ? file.name
              : lang?.videoLibraryUploadDrop || "Drag & drop or click to choose a file"}
          </Typography>
          {file ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {formatBytes(file.size)}
            </Typography>
          ) : null}
        </Box>

        <TextField
          label={lang?.videoLibraryUploadLabelTitle || "Title (optional)"}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          size="small"
          disabled={uploading}
          placeholder={lang?.videoLibraryUploadLabelPlaceholder || "My product clip"}
        />

        {uploading ? (
          <Box>
            <LinearProgress variant={progress > 0 ? "determinate" : "indeterminate"} value={progress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {lang?.videoLibraryUploading || "Uploading…"}
              {progress > 0 ? ` ${progress}%` : ""}
            </Typography>
          </Box>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose} disabled={uploading}>
            {lang?.cancel || "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!file || uploading}
            startIcon={<CloudUploadOutlined />}
          >
            {lang?.videoLibraryUploadSubmit || "Upload to library"}
          </Button>
        </Stack>
      </Stack>
    </CommonDialog>
  );
}
